import Info from '@mdi/svg/svg/information-outline.svg?react';
import { IconWithTooltip } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { type Device } from '../types/device.types';
import './Detail.sass';

interface DetailProps {
  label: string;
  value: string;
  type: 'note' | 'description' | 'location' | 'video playback' | 'page expiry';
  className?: string;
  location?: Device;
}

const Detail: React.FC<DetailProps> = ({ label, value, type, className = '' }) => {
  const locale = useLocale();

  if (type === 'note') {
    // Hyperlink telephone numbers
    const phoneNumberRegex = /[\d+(][\d\-() ]{5,13}[0-9]{4}|911/g;
    const HTML: Array<string | React.JSX.Element> = [];
    let match: RegExpExecArray | null = null;
    let index = 0;
    let counter = 0;
    while ((match = phoneNumberRegex.exec(value)) !== null) {
      const [number, start, end] = [match[0], match.index, phoneNumberRegex.lastIndex];
      HTML.push(value.substring(index, start));
      HTML.push(
        <a
          key={counter++}
          className={number === '911' ? 'emergency' : 'phone-number'}
          href={`tel:${number}`}
        >
          {number}
        </a>,
      );
      index = end;
    }
    HTML.push(value.substring(index));

    return (
      <div className={`${className} entry full-width alarm-instructions`}>
        <label>{label}</label>
        <div className="text">{HTML}</div>
      </div>
    );
  }

  if (type === 'description') {
    return (
      <div className={`${className} entry full-width`}>
        <label>{label}</label>
        <div className="text">{value}</div>
      </div>
    );
  }

  if (type === 'location') {
    return (
      <div className={`${className} entry`}>
        <label data-testid="details-location-label">{label}</label>
        <div className="text" data-testid="details-location-value">
          {value}
        </div>
      </div>
    );
  }

  // Display an informtion icon for video playback and page expiry
  const entryClass = classNames('entry', {
    'video-playback': type === 'video playback',
    'page-expiry': type === 'page expiry',
    className,
  });

  return (
    <div className={entryClass}>
      <label>
        {label}
        {type === 'video playback' && (
          <IconWithTooltip
            className="icon-tooltip"
            icon={Info}
            title={locale.spa.videoPlaybackExpiryInfo}
            fill="var(--color-turquoise-dark)"
            iconSize={20}
          />
        )}
        {type === 'page expiry' && (
          <IconWithTooltip
            className="icon-tooltip"
            icon={Info}
            title={locale.spa.pageExpiryInfo}
            fill="var(--color-turquoise-dark)"
            iconSize={20}
          />
        )}
      </label>
      <div className="text">{value}</div>
    </div>
  );
};

export default Detail;
